import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Link,
  Divider,
  IconButton,
  Stack,
  Chip,
} from '@mui/material';
import { 
  Facebook, 
  Instagram, 
  YouTube,
  Phone,
  Email,
  LocationOn,
  AccessTime,
} from '@mui/icons-material';

const Footer: React.FC = () => {
  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#212121',
        color: 'white',
        mt: 'auto',
      }}
    >
      {/* Main Footer Content */}
      <Box sx={{ backgroundColor: '#212121', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Company Info */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography 
                variant="h5" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  color: '#35C5F0',
                  mb: 3,
                }}
              >
                🏠 Dewfoam
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 3, 
                  color: 'grey.300',
                  lineHeight: 1.6,
                }}
              >
                A premium furniture brand with 25 years of tradition,<br />
                creating your special spaces.
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Chip 
                    label="Free Shipping" 
                    size="small" 
                    sx={{ 
                      backgroundColor: '#35C5F0', 
                      color: 'white',
                      fontWeight: 600,
                    }} 
                  />
                  <Chip 
                    label="Free Installation" 
                    size="small" 
                    sx={{ 
                      backgroundColor: '#FF7F2F', 
                      color: 'white',
                      fontWeight: 600,
                    }} 
                  />
                  <Chip 
                    label="Warranty Service" 
                    size="small" 
                    sx={{ 
                      backgroundColor: 'grey.700', 
                      color: 'white',
                      fontWeight: 600,
                    }} 
                  />
                </Stack>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  sx={{ 
                    backgroundColor: 'rgba(53, 197, 240, 0.1)',
                    color: '#35C5F0',
                    '&:hover': {
                      backgroundColor: 'rgba(53, 197, 240, 0.2)',
                    },
                  }}
                >
                  <Facebook />
                </IconButton>
                <IconButton 
                  sx={{ 
                    backgroundColor: 'rgba(255, 127, 47, 0.1)',
                    color: '#FF7F2F',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 127, 47, 0.2)',
                    },
                  }}
                >
                  <Instagram />
                </IconButton>
                <IconButton 
                  sx={{ 
                    backgroundColor: 'rgba(255, 0, 0, 0.1)',
                    color: '#FF0000',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 0, 0, 0.2)',
                    },
                  }}
                >
                  <YouTube />
                </IconButton>
              </Box>
            </Grid>

            {/* Quick Links */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  fontWeight: 600,
                  mb: 2,
                  color: 'white',
                }}
              >
                Categories
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Link 
                  href="/products" 
                  color="inherit" 
                  underline="none"
                  sx={{ 
                    color: 'grey.300',
                    fontSize: '0.9rem',
                    '&:hover': { 
                      color: '#35C5F0',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  All Products
                </Link>
                <Link 
                  href="/products?category=living-room" 
                  color="inherit" 
                  underline="none"
                  sx={{ 
                    color: 'grey.300',
                    fontSize: '0.9rem',
                    '&:hover': { 
                      color: '#35C5F0',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Living Room
                </Link>
                <Link 
                  href="/products?category=bedroom" 
                  color="inherit" 
                  underline="none"
                  sx={{ 
                    color: 'grey.300',
                    fontSize: '0.9rem',
                    '&:hover': { 
                      color: '#35C5F0',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Bedroom
                </Link>
                <Link 
                  href="/products?category=dining-room" 
                  color="inherit" 
                  underline="none"
                  sx={{ 
                    color: 'grey.300',
                    fontSize: '0.9rem',
                    '&:hover': { 
                      color: '#35C5F0',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Dining Room
                </Link>
                <Link 
                  href="/products?category=office" 
                  color="inherit" 
                  underline="none"
                  sx={{ 
                    color: 'grey.300',
                    fontSize: '0.9rem',
                    '&:hover': { 
                      color: '#35C5F0',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Office
                </Link>
              </Box>
            </Grid>

            {/* Customer Service */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ 
                  fontWeight: 600,
                  mb: 2,
                  color: 'white',
                }}
              >
                Customer Service
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Link 
                  href="#" 
                  color="inherit" 
                  underline="none"
                  sx={{ 
                    color: 'grey.300',
                    fontSize: '0.9rem',
                    '&:hover': { 
                      color: '#35C5F0',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Customer Center
                </Link>
                <Link 
                  href="#" 
                  color="inherit" 
                  underline="none"
                  sx={{ 
                    color: 'grey.300',
                    fontSize: '0.9rem',
                    '&:hover': { 
                      color: '#35C5F0',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Shipping Info
                </Link>
                <Link 
                  href="#" 
                  color="inherit" 
                  underline="none"
                  sx={{ 
                    color: 'grey.300',
                    fontSize: '0.9rem',
                    '&:hover': { 
                      color: '#35C5F0',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Exchange/Return
                </Link>
                <Link 
                  href="#" 
                  color="inherit" 
                  underline="none"
                  sx={{ 
                    color: 'grey.300',
                    fontSize: '0.9rem',
                    '&:hover': { 
                      color: '#35C5F0',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  FAQ
                </Link>
                <Link 
                  href="#" 
                  color="inherit" 
                  underline="none"
                  sx={{ 
                    color: 'grey.300',
                    fontSize: '0.9rem',
                    '&:hover': { 
                      color: '#35C5F0',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Privacy Policy
                </Link>
              </Box>
            </Grid>

            {/* Contact Info */}
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ 
                  fontWeight: 600,
                  mb: 2,
                  color: 'white',
                }}
              >
                Contact
              </Typography>
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LocationOn sx={{ color: '#35C5F0', fontSize: 20, mt: 0.2 }} />
                  <Typography variant="body2" sx={{ color: 'grey.300', fontSize: '0.9rem' }}>
                    123 Teheran-ro, Gangnam-gu<br />
                    Seoul, South Korea<br />
                    Postal Code 06234
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ color: '#35C5F0', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'grey.300', fontSize: '0.9rem' }}>
                    02-1234-5678
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ color: '#35C5F0', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'grey.300', fontSize: '0.9rem' }}>
                    info@dewfoam.co.kr
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <AccessTime sx={{ color: '#35C5F0', fontSize: 20, mt: 0.2 }} />
                  <Typography variant="body2" sx={{ color: 'grey.300', fontSize: '0.9rem' }}>
                    Mon-Fri 09:00 - 18:00<br />
                    Saturday 10:00 - 17:00<br />
                    Sun/Holidays Closed
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Bottom Bar */}
      <Box sx={{ backgroundColor: '#1a1a1a', py: 3 }}>
        <Container maxWidth="lg">
          <Divider sx={{ mb: 3, borderColor: 'grey.700' }} />
          
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" sx={{ color: 'grey.500', fontSize: '0.85rem' }}>
                © 2024 Dewfoam Philippines Co., Ltd. All rights reserved.
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }} sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, md: 0 } }}>
              <Stack direction="row" spacing={2} sx={{ justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Link 
                  href="#" 
                  sx={{ 
                    color: 'grey.500', 
                    fontSize: '0.8rem',
                    textDecoration: 'none',
                    '&:hover': { color: '#35C5F0' },
                  }}
                >
                  Terms of Service
                </Link>
                <Link 
                  href="#" 
                  sx={{ 
                    color: 'grey.500', 
                    fontSize: '0.8rem',
                    textDecoration: 'none',
                    '&:hover': { color: '#35C5F0' },
                  }}
                >
                  Privacy Policy
                </Link>
                <Link 
                  href="#" 
                  sx={{ 
                    color: 'grey.500', 
                    fontSize: '0.8rem',
                    textDecoration: 'none',
                    '&:hover': { color: '#35C5F0' },
                  }}
                >
                  Business Info
                </Link>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;